import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';

import { ErrorBoundary } from 'react-error-boundary';
import { useIntl } from 'react-intl';

import Button, { IconButton } from '@atlaskit/button/new';
import { cssMap, cx } from '@atlaskit/css';
import Heading from '@atlaskit/heading';
import ChevronDownIcon from '@atlaskit/icon/core/chevron-down';
import ChevronRightIcon from '@atlaskit/icon/core/chevron-right';
import WorkItemIcon from '@atlaskit/icon/core/work-item';
import { Box, Flex, Pressable, Stack, Text } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import type { JsmSuggestAndAddFieldsToRequestTypePayload } from '@atlassian/conversation-assistant-message-actions-api/jsm';
import { ChatGenericError } from '@atlassian/conversation-assistant-ui-components/ui/ChatGenericError';

import { messages } from './messages';
import { UpdateRequestTypeCardSuccess } from './UpdateRequestTypeCardSuccess';

const styles = cssMap({
	outermostWrapper: {
		display: 'flex',
		flexDirection: 'column',
		alignItems: 'flex-start',
		gap: token('space.150'),
		width: '100%',
		boxSizing: 'border-box',
		paddingTop: token('space.150'),
		paddingRight: token('space.150'),
		paddingBottom: token('space.150'),
		paddingLeft: token('space.150'),
	},
	outerColumn: {
		display: 'flex',
		flexDirection: 'column',
		alignItems: 'flex-start',
		width: '100%',
		boxSizing: 'border-box',
		borderRadius: token('radius.large'),
		boxShadow: token('elevation.shadow.raised'),
	},
	titleSection: {
		display: 'flex',
		alignItems: 'center',
		alignSelf: 'stretch',
		gap: token('space.150'),
		borderBottomWidth: token('border.width'),
		borderBottomStyle: 'solid',
		borderBottomColor: token('color.border'),
		paddingTop: token('space.200'),
		paddingRight: token('space.200'),
		paddingBottom: token('space.200'),
		paddingLeft: token('space.200'),
	},
	titleSection_collapsedEnd: {
		borderBottomLeftRadius: token('radius.large'),
		borderBottomRightRadius: token('radius.large'),
	},
	headerWorkItemIconWrap: {
		backgroundColor: token('elevation.surface'),
		borderWidth: token('border.width'),
		borderStyle: 'solid',
		borderColor: token('color.border'),
		borderRadius: token('radius.small'),
		paddingTop: token('space.050'),
		paddingRight: token('space.050'),
		paddingBottom: token('space.050'),
		paddingLeft: token('space.050'),
	},
	contentSection: {
		display: 'flex',
		flexDirection: 'column',
		alignItems: 'flex-start',
		alignSelf: 'stretch',
		height: '104px',
		boxSizing: 'border-box',
		gap: token('space.150'),
		paddingTop: token('space.200'),
		paddingRight: token('space.200'),
		paddingBottom: token('space.200'),
		paddingLeft: token('space.200'),
		overflowY: 'hidden',
	},
	contentSection_bodyExpanded: {
		height: 'auto',
		overflowY: 'visible',
	},
	contentSectionOuter: {
		position: 'relative',
		width: '100%',
		alignSelf: 'stretch',
	},
	fadeOffZone: {
		position: 'absolute',
		left: 0,
		right: 0,
		bottom: 0,
		height: '32px',
		width: '100%',
		backgroundImage: `linear-gradient(to bottom, rgba(0,0,0,0), ${token('color.background.input')} 100%)`,
		zIndex: 1,
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
	},
	showMoreButton: {
		borderRadius: token('radius.xlarge'),
		boxShadow: token('elevation.shadow.overlay'),
		font: token('font.body.small'),
		fontWeight: token('font.weight.medium'),
		color: token('color.text.subtle'),
		backgroundColor: token('color.background.input'),
		paddingBlock: token('space.050'),
		paddingInline: token('space.150'),
		zIndex: 100,
		opacity: 0,
		transition: 'opacity 0s',
		'&:hover': {
			backgroundColor: token('color.background.input.hovered'),
		},
		'&:active': {
			backgroundColor: token('color.background.input.pressed'),
		},
	},
	showMoreButtonVisible: {
		opacity: 1,
		transition: 'opacity 0.15s ease-in-out',
	},
	footerSection: {
		display: 'flex',
		justifyContent: 'flex-end',
		alignItems: 'center',
		alignSelf: 'stretch',
		gap: token('space.100'),
		borderTopWidth: token('border.width'),
		borderTopStyle: 'solid',
		borderTopColor: token('color.border'),
		backgroundColor: token('elevation.surface.raised'),
		borderBottomLeftRadius: token('radius.large'),
		borderBottomRightRadius: token('radius.large'),
		paddingTop: token('space.200'),
		paddingRight: token('space.200'),
		paddingBottom: token('space.200'),
		paddingLeft: token('space.200'),
	},
	headingWrapper: {
		overflow: 'hidden',
		textOverflow: 'ellipsis',
		minWidth: '0px',
	},
	expandToggleWrap: {
		marginInlineStart: 'auto',
	},
	fieldList: {
		marginBlockStart: '0',
		marginBlockEnd: '0',
		paddingInlineStart: token('space.250'),
		display: 'flex',
		flexDirection: 'column',
		gap: token('space.075'),
	},
});

const BodyContentShowMoreToggle = ({
	isBodyExpanded,
	onToggle,
}: {
	isBodyExpanded: boolean;
	onToggle: () => void;
}): React.JSX.Element => {
	const [isHovered, setIsHovered] = useState(false);
	const { formatMessage } = useIntl();

	return (
		<Box
			xcss={styles.fadeOffZone}
			onMouseEnter={() => setIsHovered(true)}
			onMouseLeave={() => setIsHovered(false)}
		>
			<Pressable
				xcss={cx(styles.showMoreButton, isHovered && styles.showMoreButtonVisible)}
				onClick={onToggle}
				testId="update-request-type-card.expand-body-content"
			>
				{formatMessage(isBodyExpanded ? messages.collapseBodyContent : messages.expandBodyContent)}
			</Pressable>
		</Box>
	);
};

export type UpdateRequestTypeCardProps = {
	payload: JsmSuggestAndAddFieldsToRequestTypePayload;
	onNext: () => void;
	isNextLoading?: boolean;
	isNextDisabled?: boolean;
	isSuccess?: boolean;
};

export const UpdateRequestTypeCard = ({
	payload,
	onNext,
	isNextLoading = false,
	isNextDisabled = false,
	isSuccess = false,
}: UpdateRequestTypeCardProps): React.JSX.Element => {
	const { formatMessage } = useIntl();
	const [isExpanded, setIsExpanded] = useState(true);
	const [isBodyContentExpanded, setIsBodyContentExpanded] = useState(false);
	const [hasTruncatableBodyContent, setHasTruncatableBodyContent] = useState(false);
	const contentSectionRef = useRef<HTMLDivElement>(null);

	const ExpandCollapseIcon = isExpanded ? ChevronDownIcon : ChevronRightIcon;
	const showExpandedBody = !isSuccess && isExpanded;

	const heading = formatMessage(messages.cardTitle);
	const fieldNames = payload.fieldNames ?? [];
	const fieldNamesSignature = fieldNames.join('\u0000');

	const measureBodyOverflow = useCallback(() => {
		const el = contentSectionRef.current;
		if (!el || isBodyContentExpanded) {
			return;
		}
		setHasTruncatableBodyContent(el.scrollHeight > el.clientHeight);
	}, [isBodyContentExpanded]);

	useEffect(() => {
		setIsBodyContentExpanded(false);
	}, [payload.requestTypeName, fieldNamesSignature]);

	useLayoutEffect(() => {
		measureBodyOverflow();
	}, [measureBodyOverflow, showExpandedBody, payload.requestTypeName, fieldNamesSignature]);

	useLayoutEffect(() => {
		const el = contentSectionRef.current;
		if (!el || typeof ResizeObserver === 'undefined') {
			return undefined;
		}
		const observer = new ResizeObserver(() => {
			measureBodyOverflow();
		});
		observer.observe(el);
		return () => {
			observer.disconnect();
		};
	}, [measureBodyOverflow]);

	return (
		<ErrorBoundary fallback={<ChatGenericError errorType="generic" />}>
			<Box xcss={styles.outermostWrapper} testId="update-request-type-card.root">
				{isSuccess ? (
					<UpdateRequestTypeCardSuccess
						heading={heading}
						successMessage={formatMessage(messages.successPill, {
							requestType: payload.requestTypeName ?? '',
						})}
					/>
				) : (
					<Box xcss={styles.outerColumn} testId="update-request-type-card">
						<Flex xcss={cx(styles.titleSection, !isExpanded && styles.titleSection_collapsedEnd)}>
							<Flex
								xcss={styles.headerWorkItemIconWrap}
								alignItems="center"
								justifyContent="center"
							>
								<WorkItemIcon label="" spacing="none" color={token('color.icon.accent.blue')} />
							</Flex>
							<Box xcss={styles.headingWrapper}>
								<Heading size="xsmall">{heading}</Heading>
							</Box>
							<Box xcss={styles.expandToggleWrap}>
								<IconButton
									icon={ExpandCollapseIcon}
									label={formatMessage(
										isExpanded ? messages.collapseSection : messages.expandSection,
									)}
									appearance="subtle"
									spacing="compact"
									onClick={() => setIsExpanded(!isExpanded)}
									testId="update-request-type-card.expand-toggle"
								/>
							</Box>
						</Flex>

						{showExpandedBody ? (
							<Box xcss={styles.contentSectionOuter}>
								<Box
									ref={contentSectionRef}
									xcss={cx(
										styles.contentSection,
										isBodyContentExpanded && styles.contentSection_bodyExpanded,
									)}
								>
									<Stack space="space.200">
										<Heading size="medium">{payload.requestTypeName}</Heading>
										<Stack space="space.100">
											<Text size="medium">{formatMessage(messages.fieldsToAddHeading)}</Text>
											{fieldNames.length > 0 && (
												<Box as="ul" xcss={styles.fieldList}>
													{fieldNames.map((name, index) => (
														<Box as="li" key={`${name}-${index}`}>
															<Text size="medium">{name}</Text>
														</Box>
													))}
												</Box>
											)}
										</Stack>
									</Stack>
								</Box>
								{hasTruncatableBodyContent ? (
									<BodyContentShowMoreToggle
										isBodyExpanded={isBodyContentExpanded}
										onToggle={() => setIsBodyContentExpanded(!isBodyContentExpanded)}
									/>
								) : null}
							</Box>
						) : null}

						{showExpandedBody ? (
							<Flex xcss={styles.footerSection}>
								<Button
									appearance="primary"
									onClick={onNext}
									isLoading={isNextLoading}
									isDisabled={isNextDisabled}
									testId="update-request-type-card.next"
								>
									{formatMessage(messages.nextButton)}
								</Button>
							</Flex>
						) : null}
					</Box>
				)}
			</Box>
		</ErrorBoundary>
	);
};
